// Material UI and React core imports
import { AppBar, IconButton, Toolbar, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useState } from "react";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";

// Custom components and utilities
import AccessControl from "@/components/AccessControl/AccessControl";
import { isPublicPage } from "@/utils/visualBuilder";

// Local components and constants
import ExperienceTabs from "./ExperienceTabs";
import { ELEMENT_TYPE, MEDIA_SIZE, PROPERTY_TIERS, TAB_STATUSES } from "../../constants";
import { useUrlParams } from "../../hooks";
import { CircleChecked, CircleUnchecked, ExpMenu, FullscreenModeIcon, GridModeIcon, RightMenu } from "../../icons";
import { ExperienceService } from "../../services";
import Elements from "./Elements/Elements";
import ExperienceToolbar from "./ExperienceToolbar/ExperienceToolbar";
import ExperienceTitle from "./Forms/ExperienceTitle";
import ContactAvatar from "./Header/ContactAvatar";
import ContactDialog from "./Header/ContactDialog";
import ContactDialogV2 from "./Header/ContactDialogV2";
import {
  addCurrentSelectedItems,
  resetCurrentSelectedItems,
  setSelectAll,
  setSimpleValue,
  updateVisualBuilderData,
} from "../../store/actions";
import {
  contentElementsSelector,
  currentExperienceSelector,
  currentMediaItemSelector,
  currentPropertySelector,
  currentSelectedItemsSelector,
  elementsSelector,
  experienceMediaItemsSelector,
  visualBuilderDataSelector,
} from "../../store/selectors";
import { getMediaLink } from "../../utils/mediaLink";

// Map tab statuses to numeric indices for navigation
// Used to convert string-based tab statuses to numeric indices for UI components
const tabMap = {
  [TAB_STATUSES.COLLECT]: 0,
  [TAB_STATUSES.CUSTOMIZE]: 1,
  [TAB_STATUSES.SHARE]: 2,
};

// Define styles using Material UI's makeStyles hook
const useStyles = makeStyles((theme) => ({
  // App bar styles - transparent background with transition effects
  appBar: {
    backgroundColor: "transparent",
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  // Menu button styles for the left drawer toggle
  menuButton: {
    marginTop: "0px",
    position: "absolute",
    height: "54px",
    top: "1px",
    left: "16px",
    "@media (min-width:600px)": {
      top: "5px",
      left: "24px",
    },
    zIndex: "1",
  },

  // Menu button styles for the right drawer toggle
  menuButtonLeft: {
    marginTop: "0px",
    position: "absolute",
    height: "54px",
    top: "1px",
    right: "16px",
    "@media (min-width:600px)": {
      top: "5px",
      right: "24px",
    },
    zIndex: "1",
  },

  // Grid menu button styles with transition effects
  menuButtonGrid: {
    transition: "margin 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
    marginRight: "24px",
    "@media (max-width:400px)": {
      marginRight: theme.spacing(0),
    },
  },

  // Inner menu container styles with responsive layout
  innerMenu: {
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    transition: "margin 225ms cubic-bezier(0, 0, 0.2, 1) 0ms, padding 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
    background: "linear-gradient(180deg, rgba(0, 0, 0, 0.704354) 0%, rgba(0, 0, 0, 0.0001) 100%)",
    marginLeft: (props) => (props.leftDrawer && !props.leftDrawerAutoHide && !isMobile ? 240 : 0),
    marginRight: (props) => (props.rightDrawer && !props.rightDrawerAutoHide && !isMobile ? 240 : 0),
    paddingLeft: (props) => (isMobile ? 48 : props.leftDrawerAutoHide ? 12 : props.leftDrawer ? 12 : 68),
    paddingRight: (props) =>
      isPublicPage(props.tabStatus)
        ? props.rightDrawer
          ? isMobile
            ? "68px"
            : "10px"
          : "68px"
        : !props.rightDrawerAutoHide
          ? "10px"
          : "68px",
  },

  // Mobile-specific inner menu styles
  homebaseMobileInnerMenu: {
    borderRadius: "0px 0px 0px 0px",
  },

  // Styles for inner menu placement
  innerMenuPlacement: {
    paddingLeft: (props) => props.gridButtonPlacement,
    justifyContent: "space-between",
  },

  // Select all text styles
  selectAllText: {
    color: "#fff",
    fontSize: "0.75rem",
    marginLeft: "0.5rem",
    "@media (max-width:400px)": {
      fontSize: "0.525rem",
    },
  },

  // Title text styles
  title: {
    flexGrow: 1,
    color: "#fff",
    fontSize: "1.25rem",
    marginRight: "1rem",
    "@media (max-width:400px)": {
      fontSize: "0.875rem",
      marginRight: "0.5rem",
    },
  },

  // Small icon styles
  iconSmall: {
    color: theme.palette.common.white,
    width: "15px",
  },

  // Header title section layout
  headerTitleSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    "@media (max-width:767px)": {
      flex: "0 0 50%",
      maxWidth: "50%",
    },
  },

  // Header menu section layout
  headerMenuSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    "@media (max-width:767px)": {
      flex: "0 0 50%",
      maxWidth: "50%",
      order: 3,
      justifyContent: "center",
    },
  },

  // Header icon section layout
  headerIconSection: {
    flex: 1,
  },

  // Upper toolbar styles
  upperToolbar: {
    minHeight: "40px",
    backgroundColor: "#505050",
    justifyContent: "space-between",
  },

  // White text styles
  whiteText: {
    color: theme.palette.common.white,
    fontSize: 16,
  },

  // Lower toolbar styles
  lowerToolbar: {
    display: "flex",
    flexDirection: "row",
    pointerEvents: "none",
    position: "relative",
    backgroundColor: isMobile ? "#2c2c2c" : "transparent",
    "& button": {
      pointerEvents: "auto",
    },
  },

  // Homebase lower toolbar styles
  homeBaseLowerToolbar: {
    display: "flex",
    flexDirection: "row",
    pointerEvents: "none",
    position: "relative",
    backgroundColor: isMobile ? "#2c2c2c" : "transparent",
    "& button": {
      pointerEvents: "auto",
    },
    marginTop: "40px",
  },

  // Fixed header styles
  headerFixed: {
    height: "7.5rem",
    zIndex: "2",
  },

  // Header bar styles
  headerBar: {
    height: "40px",
  },

  // Icon styles based on tab status
  icon: (props) =>
    props.tabStatus === TAB_STATUSES.HOMEBASE
      ? {
          verticalAlign: "middle",
        }
      : {
          fontSize: "1.5rem",
          fill: "none",
        },

  // Icon link styles
  iconLink: {
    marginRight: "0 !important",
  },

  // Icon options styles
  iconOptions: {
    fontSize: "2.5rem",
    stroke: "#FFF",
    fill: "none",
  },

  // Left arrow styles
  arrowLeft: {
    fontSize: "1rem",
    cursor: "pointer",
  },

  // All elements container styles
  allElements: {
    display: "flex",
    width: "100%",
    height: "100%",
    position: "absolute",
    left: 0,
    right: 0,
    flexDirection: "row-reverse",
    transition: "padding 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
    paddingRight: (props) =>
      props.rightDrawer
        ? isPublicPage(props.tabStatus) && props.experienceAvatars
          ? isMobile
            ? "128px"
            : "68px"
          : "48px"
        : isPublicPage(props.tabStatus) && !props.rightDrawerAutoHide
          ? "128px"
          : "68px",
  },

  // Play icon styles
  playIcon: {
    fontSize: "30px",
  },

  // Avatar styles
  avatar: {
    width: 30,
    height: 30,
  },
}));

/**
 * ExperienceTabMenu Component
 *
 * A complex navigation and control interface for the experience builder.
 * Manages drawer states, grid/fullscreen views, and various UI controls.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.label - Label text for the experience
 * @param {boolean} props.belowSmallWidth - Flag indicating if viewport is below small width breakpoint
 */
export default function ExperienceTabMenu(props) {
  // Extract props
  const { label = "", belowSmallWidth } = props;

  // Local state for anchor element (used for menus/popovers)
  const [anchorEl, setAnchorEl] = useState(null);

  // Initialize hooks
  const params = useUrlParams();
  const dispatch = useDispatch();

  // Extract URL parameters
  const { propertyId, experienceId = null, userId } = params;

  // Select required data from Redux store
  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));
  const { property } = useSelector(currentPropertySelector);
  const { tier = {} } = property;
  const { experience } = useSelector(currentExperienceSelector);
  const { mediaItem: currentMediaItem } = useSelector(currentMediaItemSelector);
  const { experienceMediaItems = {} } = useSelector(experienceMediaItemsSelector(propertyId, experienceId));
  const { selectAll } = useSelector(currentSelectedItemsSelector);

  // Extract additional data
  const { branding = {} } = property;
  const { elements } = useSelector(elementsSelector(propertyId, experienceId));
  const { elements: contentElements } = useSelector(contentElementsSelector(propertyId, currentMediaItem.id));

  // Extract custom settings
  const { customSettings = {} } = branding;
  const { experienceAvatars = true } = customSettings;
  const experienceMediaItemsLength = Object.keys(experienceMediaItems).length;

  // Extract visual builder settings
  const {
    leftDrawerAutoHide = true,
    rightDrawerAutoHide = false,
    leftDrawer = true,
    rightDrawer = true,
    gridView = false,
    prevTabStatus,
    filteredMediaItems = [],
    locked,
    flags,
    createdBy = {},
  } = visualBuilderData;

  const { tabStatus } = params;

  // Feature flag for tier access
  const tierAccessFlag = flags?.["tiers-phase-three"];

  // Filter elements based on tab status
  const rightElements = elements.filter((el) =>
    tabStatus === TAB_STATUSES.COLLECT ? el.type !== ELEMENT_TYPE.MAP : el,
  );

  // Combine content and right elements based on tab status
  let allElementData = tabStatus === TAB_STATUSES.HOMEBASE ? contentElements : [...contentElements, ...rightElements];

  // Calculate grid button placement based on various conditions
  const gridButtonPlacement =
    (!belowSmallWidth &&
      (tabStatus === TAB_STATUSES.COLLECT ||
        tabStatus === TAB_STATUSES.CUSTOMIZE ||
        tabStatus === TAB_STATUSES.SHARE) &&
      leftDrawer) ||
    (leftDrawer && !leftDrawerAutoHide) ||
    (tabStatus !== TAB_STATUSES.HOMEBASE && leftDrawerAutoHide)
      ? "12px"
      : "68px";

  // Initialize styles with props
  const classes = useStyles({
    ...props,
    leftDrawer,
    rightDrawer,
    leftDrawerAutoHide,
    rightDrawerAutoHide,
    gridButtonPlacement,
    tabStatus,
    experienceAvatars,
    prevTabStatus,
  });

  // Extract experience title
  const { title = "" } = experience || {};

  // Get creator's photo URL
  const photo = getMediaLink({ size: MEDIA_SIZE.tiny, link: createdBy?.photoUrl });

  // Convert tab status to numeric index
  const adjustedTabStatus = tabMap[tabStatus] || 0;

  /**
   * Determines the color based on status and media items length
   * @param {boolean} isActive - Whether the item is active
   * @returns {string} - Hex color code
   */
  const getStatusColor = (isActive) => {
    if (isActive) return "#25949D";
    return experienceMediaItemsLength < 1 ? "#f5f5f5" : "#fff";
  };

  /**
   * Handles toggling drawers (left/right)
   * @param {string} drawerSide - Which drawer to toggle ('left' or 'right')
   */
  const handleDrawer = (drawerSide) => {
    if (drawerSide === "left") {
      dispatch(updateVisualBuilderData(params, { leftDrawer: !leftDrawer }));
    } else {
      dispatch(updateVisualBuilderData(params, { rightDrawer: !rightDrawer }));
    }
  };

  /**
   * Updates the experience title
   * @param {string} title - New title for the experience
   */
  const handleTitleUpdate = async (title) => {
    if (title && experienceId) {
      await ExperienceService.updateExperience(propertyId, experienceId, {
        title,
      });
    }
  };

  /**
   * Handles selecting/deselecting all items in grid view
   * Updates Redux store with selected items
   */
  const handleSelectAll = () => {
    if (selectAll) {
      dispatch(resetCurrentSelectedItems());
    } else {
      dispatch(addCurrentSelectedItems(filteredMediaItems.reduce((a, v) => ({ ...a, [v.id]: v }), {})));
    }
    dispatch(setSelectAll(!selectAll));
  };

  // Render the component with full navigation and control interface
  return (
    <div className={classes.headerFixed} data-testid="experience-tab-menu-container">
      <AppBar
        position="fixed"
        classes={{ root: classes.appBar }}
        className={classes.headerBar}
        style={{
          ...(isPublicPage(tabStatus) && {
            boxShadow: "none",
            pointerEvents: "none",
          }),
        }}
      >
        {/* Render upper toolbar conditionally for non-homebase and non-public pages */}
        {tabStatus !== TAB_STATUSES.HOMEBASE && !isPublicPage(tabStatus) && (
          <Toolbar className={classes.upperToolbar} style={{ marginTop: tabStatus ? "40px" : "0px" }}>
            <>
              {/* Title section with editable experience title */}
              <div className={classes.headerTitleSection}>
                <ExperienceTitle label={label} handleTitleUpdate={handleTitleUpdate} title={title} disabled={locked} />
              </div>

              {/* Navigation tabs section */}
              <div className={classes.headerMenuSection}>
                <ExperienceTabs
                  propertyId={propertyId}
                  experienceId={experienceId}
                  userId={userId}
                  tabStatus={tabStatus}
                  adjustedTabStatus={adjustedTabStatus}
                  belowSmallWidth={belowSmallWidth}
                  experienceMediaItemsLength={experienceMediaItemsLength}
                  statusColor={(status) => getStatusColor(status)}
                />
              </div>
            </>
            <div className={classes.headerIconSection} />
          </Toolbar>
        )}

        {/* Conditional rendering based on single-menu-redesign flag */}
        {flags?.["single-menu-redesign"] ? (
          // New toolbar design
          <ExperienceToolbar
            allElementData={allElementData}
            gridView={gridView}
            leftDrawer={leftDrawer}
            leftDrawerAutoHide={leftDrawerAutoHide}
            filteredMediaItems={filteredMediaItems}
            locked={locked}
          />
        ) : (
          // Classic toolbar design
          <div className={tabStatus !== TAB_STATUSES.HOMEBASE ? classes.lowerToolbar : classes.homeBaseLowerToolbar}>
            {/* Left drawer toggle button */}
            {!leftDrawerAutoHide && tabStatus === TAB_STATUSES.PUBLIC_SINGLE_SHARE && (
              <IconButton
                aria-label="open drawer"
                onClick={() => handleDrawer("left")}
                edge="start"
                color="inherit"
                className={classes.menuButton}
                size="large"
              >
                <ExpMenu className={classes.icon} />
              </IconButton>
            )}

            {/* Main toolbar content */}
            <Toolbar
              className={
                tabStatus !== TAB_STATUSES.HOMEBASE
                  ? `${classes.innerMenu} ${isMobile ? classes.homebaseMobileInnerMenu : ""}`
                  : `${classes.innerMenu} ${classes.innerMenuPlacement}`
              }
            >
              {/* Grid/Fullscreen view toggle button */}
              <IconButton
                className={classes.menuButtonGrid}
                onClick={() =>
                  dispatch(
                    updateVisualBuilderData(params, {
                      gridView: !gridView,
                      currentMediaItemId: gridView ? filteredMediaItems[0]?.id : null,
                    }),
                  )
                }
                size="large"
              >
                {gridView ? <GridModeIcon /> : <FullscreenModeIcon />}
              </IconButton>

              {/* Select all button for grid view with access control */}
              {gridView && ![TAB_STATUSES.PUBLIC_SINGLE_SHARE, TAB_STATUSES.PUBLIC].includes(tabStatus) ? (
                <AccessControl
                  allowedPermissions={["experiences:create"]}
                  allowedTiers={tierAccessFlag ? [PROPERTY_TIERS.PRO] : []}
                >
                  <IconButton
                    data-testid="select-all-button"
                    className={classes.menuButtonGrid}
                    onClick={handleSelectAll}
                    size="large"
                    sx={{ pt: "14px", pb: "10px" }}
                    disabled={locked}
                  >
                    {selectAll ? <CircleChecked /> : <CircleUnchecked />}
                    <Typography variant="body1" classes={{ root: classes.selectAllText }}>
                      Select All
                    </Typography>
                  </IconButton>
                </AccessControl>
              ) : (
                ""
              )}

              {/* Current media item name display */}
              <Typography variant="body1" classes={{ root: classes.title }}>
                {!gridView && currentMediaItem.name}
              </Typography>

              {/* Elements container for non-grid view */}
              {!gridView && (
                <div className={classes.allElements}>
                  <Elements anchorEl={anchorEl} setAnchorEl={setAnchorEl} elements={allElementData} />
                </div>
              )}

              {/* Contact avatar for specific tab statuses and tiers */}
              {![
                TAB_STATUSES.HOMEBASE,
                TAB_STATUSES.COLLECT,
                TAB_STATUSES.CUSTOMIZE,
                TAB_STATUSES.PUBLIC_MEDIA_SHARE,
              ].includes(tabStatus) &&
                tier?.title &&
                [PROPERTY_TIERS.PRO, PROPERTY_TIERS.STANDARD].includes(tier?.title) &&
                experienceAvatars && (
                  <ContactAvatar
                    action={() => dispatch(setSimpleValue("contact_salesperson", true))}
                    classes={classes}
                    photoURL={photo}
                  />
                )}
            </Toolbar>

            {/* Right drawer toggle button */}
            {!rightDrawerAutoHide && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="open drawer"
                onClick={() => handleDrawer("right")}
                className={classes.menuButtonLeft}
                size="large"
              >
                <RightMenu />
              </IconButton>
            )}
          </div>
        )}
      </AppBar>
      {flags?.["digital-business-card"] ? (
        <ContactDialogV2 documentName="salesperson" createdBy={createdBy} />
      ) : (
        <ContactDialog documentName="salesperson" createdBy={createdBy} />
      )}
    </div>
  );
}
